import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildCustomerPaymentPrint } from "@/modules/docs/print/doc-print-math";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { getCustomerPayment, voidCustomerPayment } from "@/shared/api/sales";
import { customerPaymentAllocationColumns } from "./so-detail-columns";

export function CustomerPaymentDetailPage() {
  const { companySlug, paymentId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getCustomerPayment>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (!companySlug || !paymentId) return;
    setData(await getCustomerPayment(companySlug, paymentId));
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !paymentId) return;
    getCustomerPayment(companySlug, paymentId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, paymentId, navigate]);

  useRecordRecentDoc({
    companySlug,
    kind: "customerPayment",
    id: data?.payment.id,
    label: data?.payment.number,
    path:
      companySlug && data?.payment.id
        ? `/c/${companySlug}/sales/payments/${data.payment.id}`
        : undefined,
    hint: data?.payment.customerName,
  });

  async function onVoid() {
    if (!companySlug || !paymentId) return;
    const ok = await confirmUi({
      title: "Void this payment?",
      description:
        "AR and cash/bank will reverse. The number is kept (gap OK).",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await voidCustomerPayment(companySlug, paymentId, {
        idempotencyKey: `cp-void-${crypto.randomUUID()}`,
      });
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !paymentId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading payment…</Alert>
        )}
      </PageFrame>
    );
  }

  const { payment: pay, allocations } = data;
  const directionLabel =
    pay.direction === "to_customer" ? "refund / credit" : "from customer";
  const printData = buildCustomerPaymentPrint({
    number: pay.number,
    customerName: pay.customerName,
    lifecycle: pay.lifecycle,
    direction: pay.direction,
    amount: pay.amount,
    payMethodName: pay.payMethodName,
    payMethodCode: pay.payMethodCode,
    bankRef: pay.bankRef,
    issuedAt: pay.postedAt,
    allocations: allocations.map((a) => ({
      soNumber: a.soNumber,
      amount: a.amount,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/sales/payments`}
            listLabel="Customer payments"
          />
        </div>
        <PageHeader
          eyebrow="Customer payment"
          title={pay.number}
          description={[
            pay.lifecycle,
            directionLabel,
            pay.customerName,
            pay.payMethodName || pay.payMethodCode,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {pay.lifecycle === "posted" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void payment
                </Button>
              )}
            </>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="customer_payment"
          docId={paymentId}
          linkPath={`/c/${companySlug}/sales/payments/${paymentId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Amount</dt>
        <dd className="m-0 font-mono font-medium">{pay.amount}</dd>
        <dt className="text-[var(--color-muted)]">Customer</dt>
        <dd className="m-0">
          <Link
            to={`/c/${companySlug}/contacts?role=customer`}
            className="no-underline"
          >
            {pay.customerName}
          </Link>
        </dd>
        {(pay.payMethodName || pay.payMethodCode) && (
          <>
            <dt className="text-[var(--color-muted)]">Method</dt>
            <dd className="m-0">
              {pay.payMethodName ?? pay.payMethodCode}
              {pay.payMethodCode && pay.payMethodName ? (
                <span className="font-mono text-xs text-[var(--color-muted)]">
                  {" "}
                  · {pay.payMethodCode}
                </span>
              ) : null}
            </dd>
          </>
        )}
        {pay.bankRef && (
          <>
            <dt className="text-[var(--color-muted)]">Bank / ref</dt>
            <dd className="m-0">{pay.bankRef}</dd>
          </>
        )}
        {pay.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{pay.postedAt}</dd>
          </>
        )}
      </dl>

      <section className="mt-6 border-t border-[var(--color-border)]/90 pt-4">
        <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          Allocated to
        </h2>
        <DataTable
          listId="customer-payment-allocations"
          companySlug={companySlug}
          className="mt-3"
          columns={customerPaymentAllocationColumns(companySlug)}
          data={allocations}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No allocations"
              description="This payment is not allocated to a sales order."
            />
          }
        />
      </section>

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="customer_payment"
        docId={paymentId}
        linkPath={`/c/${companySlug}/sales/payments/${paymentId}`}
      />
    </PageFrame>
  );
}
